import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckThenRun, OuPrompt, OuPromptContext, OuSwitchPromptProps, UseOuSwitchPromptProps } from './types';
import { useStore } from '30.quickConnect.Stores';
import { OuDecisionKind } from '90.quickConnect.Models/enums';
import { OuDecision } from '90.quickConnect.Models/models/user/ouDecision';
import { errorHandler } from '80.quickConnect.Core/helpers';

const tag = '10.quickConnect.app/components/shared/Dialogs/OuSwitchPrompt/useOuSwitchPrompt.ts';

/**
 * Applique une décision d'OuAccessStore (portage de OUAccessChecker.checkThenRun / OUSwitchPrompt Android) :
 * exécute l'action directement, après confirmation du changement d'UO (sans confirmation si l'utilisateur a choisi la
 * bascule automatique), ou affiche le refus. Les popups sont rendues par OuSwitchPrompt avec promptProps.
 */
const useOuSwitchPrompt = (): UseOuSwitchPromptProps => {
  const {
    LoginStore: { setCurrentOuCode },
    UserSettingsStore: { autoSwitchOU },
  } = useStore();
  const { t } = useTranslation(['common', 'declaration', 'inboxes']);

  const [pending, setPending] = useState<{ prompt: OuPrompt; onRefused?: () => void }>();
  const isCheckingRef = useRef(false);

  const getSwitchMessage = useCallback(
    (context: OuPromptContext, ouName: string): string => {
      switch (context) {
        case 'declaration':
          return `${t('qcapp_alert_dialog_switch_to_declaration_ou_message', { ns: 'declaration' })}${ouName}`;
        case 'inbox':
          return `${t('inbox_cannot_open_inbox_change_ou', { ns: 'inboxes' })}${ouName}`;
        default:
          return `${t('qcapp_declaration_not_in_current_ou', { ns: 'declaration' })}${ouName}`;
      }
    },
    [t],
  );

  const checkThenRun = useCallback<CheckThenRun>(
    async (decisionProvider, action, { context, onRefused, onUnauthorized, onDeleteInbox }) => {
      // Un seul contrôle à la fois (double clic pendant les appels réseau)
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;

      let decision: OuDecision;
      try {
        decision = await decisionProvider();
      } catch (error) {
        // Le contrôle ne doit pas bloquer l'utilisateur : ouverture comme sans la feature
        errorHandler(tag, error, 'checkThenRun', 'warn');
        decision = { kind: OuDecisionKind.Open };
      } finally {
        isCheckingRef.current = false;
      }

      switch (decision.kind) {
        case OuDecisionKind.Open:
          await action();
          return;
        case OuDecisionKind.SwitchTo:
          if (autoSwitchOU) {
            setCurrentOuCode(decision.ou.code);
            await action();
            return;
          }
          setPending({
            prompt: {
              kind: 'switch',
              message: getSwitchMessage(context, decision.ou.name),
              ouCode: decision.ou.code,
              action,
            },
            onRefused,
          });
          return;
        case OuDecisionKind.Unauthorized:
          if (onUnauthorized) {
            onUnauthorized();
            return;
          }
          setPending({
            prompt: onDeleteInbox ? { kind: 'deleteInbox', deleteInbox: onDeleteInbox } : { kind: 'unauthorized' },
            onRefused,
          });
      }
    },
    [autoSwitchOU, setCurrentOuCode, getSwitchMessage],
  );

  const confirm = useCallback(async () => {
    if (!pending) return;
    const { prompt } = pending;
    setPending(undefined);
    try {
      if (prompt.kind === 'switch') {
        setCurrentOuCode(prompt.ouCode);
        await prompt.action();
      } else if (prompt.kind === 'deleteInbox') {
        await prompt.deleteInbox();
      }
    } catch (error) {
      errorHandler(tag, error, 'confirm', 'error');
    }
  }, [pending, setCurrentOuCode]);

  const refuse = useCallback(() => {
    const onRefused = pending?.onRefused;
    setPending(undefined);
    onRefused?.();
  }, [pending]);

  const promptProps = useMemo<OuSwitchPromptProps>(
    () => ({ prompt: pending?.prompt, confirm, refuse }),
    [pending, confirm, refuse],
  );

  return { checkThenRun, promptProps };
};

export default useOuSwitchPrompt;
